import { useState } from 'react';
import {
  Card,
  Form,
  InputNumber,
  Input,
  Button,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  submitTraining,
  getJob,
  listModels,
  type LoRAModel,
  type LoRATrainingJob,
} from '@/api/lora';

const { Title, Paragraph } = Typography;

/**
 * LoRA 训练管线页 (content-scale-lora).
 *
 * 提交训练 → WF_LORA_TRAIN (stub provider, 秒级完成) → 产物注册为 lora_models.
 * 训练好的 LoRA 可在套系详情绑定, 让该套系出图带训练风格.
 * 真实 GPU 训练随凭证落地; 现在 stub 可端到端跑通.
 */
export function LoRAListPage() {
  const [appId, setAppId] = useState<number | null>(9);
  const [loraName, setLoraName] = useState('');
  const [dataset, setDataset] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [models, setModels] = useState<LoRAModel[]>([]);
  const [lastJob, setLastJob] = useState<LoRATrainingJob | null>(null);

  const refreshModels = async (id: number) => {
    try {
      const res = await listModels(id);
      setModels(res.data || []);
    } catch {
      setModels([]);
    }
  };

  const onSubmit = async () => {
    if (!appId || !loraName) {
      message.error('app_id 和 lora_name 必填');
      return;
    }
    setSubmitting(true);
    try {
      const job = await submitTraining({ app_id: appId, lora_name: loraName, dataset_ref: dataset, base_model: 'flux-kontext' });
      setLastJob(job);
      message.success(`训练任务已提交: ${job.id}`);
      // poll a few times (stub completes in ~2s)
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const j = await getJob(job.id);
        setLastJob(j);
        if (j.status === 'succeeded' || j.status === 'failed') break;
      }
      await refreshModels(appId);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Title level={4} style={{ margin: 0 }}>
        LoRA 训练管线
      </Title>
      <Paragraph type="secondary">
        提交训练 → WF_LORA_TRAIN → 产物注册为 lora_models（可在套系详情绑定）。当前 stub provider
        秒级完成；真实 GPU 训练（PAI / OneThingAI）随凭证落地。
      </Paragraph>

      <Form layout="inline" style={{ marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
        <Form.Item label="App ID">
          <InputNumber min={1} value={appId ?? undefined} onChange={(v) => setAppId(typeof v === 'number' ? v : null)} />
        </Form.Item>
        <Form.Item label="LoRA 名">
          <Input value={loraName} onChange={(e) => setLoraName(e.target.value)} placeholder="touliu-hk80s-v2" style={{ width: 220 }} />
        </Form.Item>
        <Form.Item label="数据集">
          <Input value={dataset} onChange={(e) => setDataset(e.target.value)} placeholder="ds://..." style={{ width: 180 }} />
        </Form.Item>
        <Button type="primary" loading={submitting} onClick={onSubmit}>
          提交训练
        </Button>
        <Button onClick={() => appId && refreshModels(appId)}>刷新产物</Button>
      </Form>

      {lastJob && (
        <Paragraph>
          最近任务 <code>{lastJob.id}</code>：
          <Tag color={lastJob.status === 'succeeded' ? 'green' : lastJob.status === 'failed' ? 'red' : 'blue'} style={{ marginLeft: 8 }}>
            {lastJob.status}
          </Tag>
          {lastJob.error && <span style={{ color: '#cf1322' }}> {lastJob.error}</span>}
        </Paragraph>
      )}

      <Title level={5}>已训练 LoRA（lora_models）</Title>
      <Table<LoRAModel>
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={models}
        columns={[
          { title: 'id', dataIndex: 'id' },
          { title: 'name', dataIndex: 'name' },
          {
            title: 'status',
            dataIndex: 'status',
            render: (s: string) => <Tag color={s === 'ready' ? 'green' : s === 'failed' ? 'red' : 'blue'}>{s}</Tag>,
          },
          { title: 'provider', dataIndex: 'provider' },
          { title: 'trigger_word', dataIndex: 'trigger_word' },
        ]}
      />
      <Space style={{ marginTop: 12 }}>
        <Button onClick={() => appId && refreshModels(appId)} size="small">
          加载 App {appId} 的 LoRA
        </Button>
      </Space>
    </Card>
  );
}
